import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { removeFcmTokenOnLogout } from '@/utils/pushUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { Spinner } from './spinner/spinner';
import { useTranslation } from 'react-i18next';
import LanguageDropdown from '@/components/selectLanguage';
import { api } from '@/utils/api';
import { isWorkerProfileComplete } from '@/utils/workerProfileUtils';
import { WorkerProfile } from '@/types/userTypes';
interface AvatarShellDropdownProps {
  user: any;
  onLogout: () => void;
}

const AvatarShellDropdown: React.FC<AvatarShellDropdownProps> = ({ user, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(true);
  const [workerProfileData, setWorkerProfileData] = useState<WorkerProfile>();

  // Check worker profile completeness (same logic as sidebar)
  useEffect(() => {
    const fetchWorkerProfile = async () => {
      if (user?.type === 'worker') {
        try {
          const workerProfile = await api.getMyWorkerProfile();
          if (workerProfile) {
            setWorkerProfileData(workerProfile);
            // Check if profile is complete
            const complete = isWorkerProfileComplete(workerProfile);
            setIsProfileComplete(complete);
          }
        } catch (err) {
          console.error('Error fetching worker profile:', err);
          // Default to true if fetch fails to avoid blocking
          setIsProfileComplete(true);
        }
      } else {
        // For non-workers, always set to true (no restrictions)
        setIsProfileComplete(true);
      }
    };

    fetchWorkerProfile();
  }, [user?.type]);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleDashboard = () => {
    // Prevent navigation if worker profile is incomplete
    if (user?.type === 'worker' && !isProfileComplete) {
      return;
    }
    
    handleMenuClose();
    if (user?.type === 'worker') {
      navigate('/my-jobs');
    } else {
      // TODO: Add client dashboard route here
      navigate('/my-proposals');
    }
  };
  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };
  const handleLogoutClick = async () => {
    setIsLoading(true);
    await removeFcmTokenOnLogout()
    handleMenuClose();
    onLogout();
    setIsLoading(false);
  };
  // 
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      {/* language section start */}
      <LanguageDropdown />
      {/* language section end */}
      <Box
        className='shadow-none'
        onClick={handleMenuOpen}
        sx={{
          display: 'flex',

          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 3,
          px: 2,
          py: 1,
          boxShadow: 2,
          cursor: 'pointer',
          gap: 2,
          flexWrap: 'nowrap',
        }}
      >

        <div className='flex flex-col items-start'>
          <p className='text-lg font-semibold text-black w-auto capitalize sm:max-w-[200px] max-w-[100px] text-ellipsis overflow-hidden whitespace-nowrap'>
            {user?.first_name + " " + user?.last_name}
          </p>
          <span className='text-xs text-gray-500'>{user?.type === 'worker' ? t('worker') : t('client')}</span>

        </div>
        <Avatar
          src={user?.data?.avatar || user?.avatar || ''}
          alt={user?.first_name || user?.email || 'Usuario'}
          sx={{ width: 45, height: 45 }}
        />

      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: 3,
            p: 0.5,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={handleDashboard} 
          disabled={user?.type === 'worker' && !isProfileComplete}
          sx={{ 
            borderRadius: 1, 
            mb: 0.5, 
            fontWeight: 500, 
            bgcolor: 'rgba(66,133,244,0.07)',
            ...(user?.type === 'worker' && !isProfileComplete && {
              opacity: 0.5,
              cursor: 'not-allowed',
            })
          }}
        >
          {t('panel')}
        </MenuItem>
        <MenuItem onClick={handleSettings} sx={{ borderRadius: 1, mb: 0.5 }}>
          {t('settings')}
        </MenuItem>
        <MenuItem disabled={isLoading} onClick={handleLogoutClick} sx={{ borderRadius: 1 }}>
          <div className='flex items-center gap-2'>
            <Spinner size={'xsmall'} isLoading={isLoading} /> {t('logout')}
          </div>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AvatarShellDropdown; 